import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ThemeButton from 'Components/ThemedButton';
import { EnumButtonSize, EnumTheme } from 'constants/enums';
import { colors } from 'constants/colors';
import CloseButtonIcon from 'Components/Icon/CloseButtonIcon';
import { Flex } from '@igooods/ui-kit';
import { useOnClickOutside } from 'utils/hooks';
import { mediaSmDown } from 'theme/helpers/css';
import { Transitions } from 'theme';
import { addStyleIfPropTruly } from 'utils/styledComponents';
import BlockContainer from 'Components/landings/BlockContainer';

export const HEADER_DROPDOWN_CONTAINER_TEST_ID = 'HEADER_DROPDOWN_CONTAINER_TEST_ID';
export const HEADER_DROPDOWN_CONTENT_TEST_ID = 'HEADER_DROPDOWN_CONTENT_TEST_ID';
export const HEADER_DROPDOWN_CLOSE_BTN_TEST_ID = 'HEADER_DROPDOWN_CLOSE_BTN_TEST_ID';
export const HEADER_DROPDOWN_TOGGLE_TEST_ID = 'HEADER_DROPDOWN_TOGGLE_TEST_ID';

interface IDropdownContent {
  $isOpen?: boolean;
  $currentTop?: number;
  $top?: string;
  $fullHeight: boolean;
}

const DropdownContent = styled(Flex).attrs({
  width: '100%',
  column: true,
  middle: true,
})<IDropdownContent>`
  position: absolute;
  left: 0;
  overflow-y: auto;
  transition: ${Transitions.transform}, ${Transitions.opacity};
  background-color: ${colors.white};
  z-index: 0;
  opacity: 0;
  transform: translateY(-10px);
  visibility: hidden;
  align-items: flex-start;

  ${({ $top }) => `
    top: calc(${$top} - 1px);
    max-height: calc(100vh - ${$top});
  `}

  ${addStyleIfPropTruly(
    '$isOpen',
    `
      transform: translateY(0);
      opacity: 1;
      z-index: 4;
      visibility: visible;
    `
  )}

  ${addStyleIfPropTruly(
    '$fullHeight',
    `
      height: 100vh;
    `
  )}

  ${mediaSmDown`
    padding-bottom: 50px;
    height: 100vh;
  `}
`;

const Block = styled(BlockContainer)`
  height: 100%;
`;

const ContentWrapper = styled(Flex).attrs({
  pt: { xs: 1, sm: 20 },
  px: { xs: 0.5, sm: 0 },
  pb: { xs: 10, sm: 20 },
  grow: true,
})`
  height: 100%;
`;

const Content = styled(Flex)`
  flex: 1 1 auto;
`;

const CloseBtn = styled(ThemeButton).attrs({ icon: true })`
  position: absolute;
  top: 40px;
  right: 24px;
  z-index: 10;

  ${mediaSmDown`
    top: 8px;
    right: 24px;
  `}
`;

interface IHeaderDropdown {
  isOpen?: boolean;
  children: React.ReactNode | React.ReactChild;
  className?: string;
  toggle?: React.ReactChild | React.ReactNode;
  onHide: () => void;
  onToggle?: () => void;
  top?: string;
  fullHeight?: boolean;
  testId?: string;
  withCloseBtn?: boolean;
  withHideOnClickOutside?: boolean;
}

const HeaderDropdown: FC<IHeaderDropdown> = ({
  className,
  toggle,
  children,
  onHide,
  onToggle,
  top = '100%',
  isOpen = false,
  fullHeight = false,
  testId = HEADER_DROPDOWN_CONTAINER_TEST_ID,
  withCloseBtn = false,
  withHideOnClickOutside = true,
}) => {
  const containerNode = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnEsc = (e: KeyboardEvent) => e.key === 'Escape' && onHide && onHide();

    const startListenEsc = () => document.addEventListener('keydown', closeOnEsc, false);
    const stopListenEsc = () => document.removeEventListener('keydown', closeOnEsc, false);

    if (isOpen && onHide) {
      startListenEsc();
    } else {
      stopListenEsc();
    }
    return stopListenEsc;
  }, [isOpen, onHide]);

  useOnClickOutside(containerNode, onHide, withHideOnClickOutside);

  return (
    <div className={className} data-testid={testId} ref={containerNode}>
      {toggle && (
        <div data-testid={HEADER_DROPDOWN_TOGGLE_TEST_ID} onClick={onToggle}>
          {toggle}
        </div>
      )}
      <DropdownContent
        data-testid={HEADER_DROPDOWN_CONTENT_TEST_ID}
        $isOpen={isOpen}
        $top={top}
        $fullHeight={fullHeight}
      >
        <Block>
          <ContentWrapper>
            {withCloseBtn && onHide && (
              <CloseBtn
                data-testid={HEADER_DROPDOWN_CLOSE_BTN_TEST_ID}
                onClick={onHide}
                use={EnumTheme.beveledMint}
                size={EnumButtonSize.normal}
                icon
              >
                <CloseButtonIcon />
              </CloseBtn>
            )}
            <Content>{children}</Content>
          </ContentWrapper>
        </Block>
      </DropdownContent>
    </div>
  );
};

export default HeaderDropdown;
